
import { makeStyles } from '@material-ui/core';
import theme from 'theme/theme';
import ForecastItem from './ForecastItem';
import { useGetCurrentUserQuery, useGetForecastsQuery } from 'utils/wemble-api';
import { useEffect, useRef, useState } from 'react';
import { getToday } from './utils';

import moment from 'moment-timezone';
moment.tz.setDefault("utc");

const useStyles = (scrollbar) => makeStyles(() => ({
  root: {
    ...(scrollbar ? {
      '&::-webkit-scrollbar': {
        'width': '7px',
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: '8px',
        backgroundColor: 'rgba(0, 0, 0, .12)',
        boxShadow: '0 0 1px rgba(255, 255, 255, .5)',
      },
      scrollbarWidth: 'auto',
      marginBottom: '-12px',
      paddingBottom: '12px',
      height: 'calc(205px + 12px + 12px + 10px)',
    } : { height: 'calc(215px + 12px + 12px)', }),
    overflowX: 'scroll',
    overflowY: 'visible',
    margin: -12,
    padding: 12,
    zIndex: 100
  },
  forecastsContainer: {
    height: '100%',
    display: 'flex',
    '& > *:not(:first-child)': {
      marginLeft: '12px',
      [theme.breakpoints.down('sm')]: {
        marginLeft: '8px'

      }
    },
  },


}));


const Forecasting = ({
}: {
  }) => {

  const { data: user } = useGetCurrentUserQuery();
  const { data: allForecasts } = useGetForecastsQuery({}, { refetchOnReconnect: true });
  const userForecasts = allForecasts?.filter(forecast => forecast.user === user?._id);

  const forecastsContainerRef = useRef<HTMLHeadingElement>(null);

  const [scrollbar, setScrollbar] = useState<boolean>(false);


  useEffect(() => {
    setScrollbar((forecastsContainerRef.current?.scrollWidth || 0) - (forecastsContainerRef.current?.clientWidth || 0) > 10)
  }, [forecastsContainerRef.current?.scrollWidth]);

  const classes = useStyles(scrollbar)();


  const missingForecast = (lastDate: string | undefined) => {
    const nextDate = moment(lastDate).add(1, 'day');

    return !forecasts.some(f => {
      const startDate = moment(f.startDate)
      const diff = startDate.diff(nextDate, 'days')
      return startDate.isSame(nextDate, 'day') || (startDate.day() == 1 && diff <= 3 && diff >= 0)
    }) ? nextDate : undefined
  }

  const today = getToday()


  const forecasts = userForecasts ? [...userForecasts].filter((forecast) => moment(forecast.endDate) >= moment(today)).sort((a, b) => new Date(a.startDate!).getTime() - new Date(b.startDate!).getTime()) : []
  const currentForecast = forecasts.find(forecast => moment(forecast.startDate) <= moment(today))

  // Calculate total number of items that will be rendered (for styling purposes)
  let totalItems = forecasts.length;

  // Add 1 if we're showing the "today" item (when no current forecast exists)
  if (!currentForecast) {
    totalItems += 1;
    // Add 1 more if there's a missing forecast after today
    if (missingForecast(today)) {
      totalItems += 1;
    }
  }

  // Add 1 for each missing forecast gap between existing forecasts
  forecasts.forEach((forecast) => {
    if (missingForecast(forecast.endDate)) {
      totalItems += 1;
    }
  });

  // Determine if we should use small forecast styling (when we have many items)
  const useSmallForecasts = totalItems >= 5;

  // Flat keyed list so the first slot — whatever covers today — keeps a stable key
  // ("today"), whether it's the synthetic tile or any real forecast covering today.
  // React then preserves the component instance when a comment typed on the synthetic
  // tile creates a forecast (textarea keeps focus), and when a today-forecast is
  // extended or its optimistic temp id is swapped for the real one.
  const items: JSX.Element[] = [];
  if (!currentForecast) {
    items.push(<ForecastItem key="today" today useSmallForecasts={useSmallForecasts} />);
    const gap = missingForecast(today);
    if (gap) items.push(<ForecastItem key={`gap-${gap.format('YYYY-MM-DD')}`} nextDate={gap} useSmallForecasts={useSmallForecasts} />);
  }
  forecasts.forEach((forecast) => {
    items.push(<ForecastItem key={forecast === currentForecast ? 'today' : forecast._id} data={forecast} useSmallForecasts={useSmallForecasts} />);
    const gap = missingForecast(forecast.endDate);
    if (gap) items.push(<ForecastItem key={`gap-${gap.format('YYYY-MM-DD')}`} nextDate={gap} useSmallForecasts={useSmallForecasts} />);
  });

  return (
    <div className={classes.root} >

      <div className={classes.forecastsContainer} ref={forecastsContainerRef}>
        {items}
      </div>



    </div>
  );
};

export default Forecasting;
